import type { NitroFetchOptions, NitroFetchRequest } from 'nitropack'
import { useDedupeFetch } from '~/composables/useDedupeFetch'
import { isProductionApiBase } from '~/utils/apiEnvironment'

type ApiOptions = NitroFetchOptions<NitroFetchRequest> & { key?: string }

const decodeCookieValue = (rawValue: string) => {
  const value = decodeURIComponent(rawValue)
  if (value === 'undefined') {
    return undefined
  }
  // Nuxt serializes cookie strings as JSON; decode SSR values the same way.
  try {
    const parsed = JSON.parse(value)
    if (typeof parsed === 'number' && String(parsed) !== value) {
      return value
    }
    return parsed
  } catch (e) {
    return value
  }
}

export const readCookieValue = (cookieName: string, defaultValue = '' as string | null | undefined) => {
  let ret
  if (useIsBrowser()) {
    try {
      ret = useCookie(cookieName, { default: () => defaultValue }).value
      return ret
    } catch (e) {}
  }
  const cookieString = useRequestHeaders(['cookie']).cookie || defaultValue || ''
  const parsedRawValue = cookieString.match(new RegExp(`${cookieName}=([^;]+)`))?.[1]
  ret = parsedRawValue ? decodeCookieValue(parsedRawValue) : defaultValue
  return ret
}

export const useAPI = () => {
  const config = useRuntimeConfig().public
  const apiBaseCookie = config.isDev
    ? useCookie<string | null>('apiBase', { default: () => null, path: '/', sameSite: 'lax', maxAge: 60 * 60 * 24 * 180 })
    : undefined
  // In dev the browser defaults to /api, so phones on the LAN also reach the backend through the dev server proxy.
  const getApiBase = () => config.isDev
    ? apiBaseCookie?.value || (useIsBrowser() ? config.publicAPI : config.ssrAPI)
    : '/api'
  // Dev pages that talk to a non-loopback backend show a red frame. Both browser and SSR bases count.
  const isProductionApi = () => Boolean(config.isDev) && (
    isProductionApiBase(apiBaseCookie?.value || config.publicAPI, config.localAPI)
    || (!apiBaseCookie?.value && isProductionApiBase(config.ssrAPI, config.localAPI))
  )

  const SRC = 'PP3'

  const getHeaders = () => {
    const inBrowser = useIsBrowser()
    const ret: Record<string, string> = {
      'x-panda-src': SRC + '-' + useRuntimeConfig().public.release,
      'x-panda-authorization': useVisitorSession().jwt_token || readCookieValue('jwt_token', ''),
      'x-panda-ssr': inBrowser ? 'NO' : 'YES',
    }
    if (!inBrowser) {
      const clientIP = useRequestHeaders(['x-forwarded-for'])['x-forwarded-for'] || useRequestEvent()?.context.clientAddress
      if (clientIP) {
        ret['x-forwarded-for'] = clientIP
      }
    }
    return ret
  }
  const useAPIPost = <T = any>(request: NitroFetchRequest, opts: ApiOptions = {}) => {
    return $fetch<T>(request, {
      baseURL: getApiBase(),
      method: 'POST',
      body: opts?.body,
      headers: getHeaders(),
      ...opts,
    })
  }

  const useAPIGet = <T = any>(request: NitroFetchRequest, opts: ApiOptions = {}) => {
    return useDedupeFetch<T>(request, {
      baseURL: getApiBase(),
      headers: getHeaders(),
      ...opts,
    })
  }

  const useAPIDelete = <T = any>(request: NitroFetchRequest, opts: ApiOptions = {}) => {
    return $fetch<T>(request, {
      baseURL: getApiBase(),
      method: 'DELETE',
      headers: getHeaders(),
      ...opts,
    })
  }

  const useAPIPut = <T = any>(request: NitroFetchRequest, opts: ApiOptions = {}) => {
    return $fetch<T>(request, {
      baseURL: getApiBase(),
      method: 'PUT',
      body: opts?.body,
      headers: getHeaders(),
      ...opts,
    })
  }
  return {
    post: useAPIPost,
    get: useAPIGet,
    delete: useAPIDelete,
    put: useAPIPut,
    apiBase: getApiBase,
    getApiBase,
    isProductionApi,
  }
}
